import React, { useCallback, useEffect, useState } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { reportsApi } from '../../services/mybudgetApi';
import { MonthlyReport } from '../../types';
import { Card } from '../../components/shared/Card';
import { Loading } from '../../components/shared/Loading';
import { Select } from '../../components/shared/Select';
import { CategoryBadge } from '../../components/shared/CategoryBadge';
import {
  formatCurrency,
  formatMonthYear,
  MONTHS,
} from '../../constants';
import { EXPENSE_TYPE_LABELS } from '../../types';

ChartJS.register(ArcElement, Tooltip, Legend);

export function MonthlyPage() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [report, setReport] = useState<MonthlyReport | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setReport(await reportsApi.monthly(year, month));
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    load();
  }, [load]);

  const chartData = report
    ? {
        labels: report.categoryBreakdown.map((c) => c.category.name),
        datasets: [
          {
            data: report.categoryBreakdown.map((c) => c.total),
            backgroundColor: report.categoryBreakdown.map((c) => c.category.color),
            borderWidth: 0,
          },
        ],
      }
    : null;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-100">Visão Mensal</h2>
          <p className="text-sm text-gray-400">
            Despesas reais e projetadas do mês
          </p>
        </div>
        <div className="flex gap-3">
          <Select
            options={Array.from({ length: 5 }, (_, i) => ({
              value: now.getFullYear() - i,
              label: String(now.getFullYear() - i),
            }))}
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          />
          <Select
            options={MONTHS.map((m, i) => ({ value: i + 1, label: m }))}
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
          />
        </div>
      </div>

      {loading ? (
        <Loading />
      ) : report ? (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Card glow="income">
              <p className="text-sm text-gray-400">Receitas</p>
              <p className="mt-2 text-2xl font-bold text-income">
                {formatCurrency(report.totalIncome)}
              </p>
            </Card>
            <Card glow="expense">
              <p className="text-sm text-gray-400">Despesas</p>
              <p className="mt-2 text-2xl font-bold text-expense">
                {formatCurrency(report.totalExpenses)}
              </p>
            </Card>
            <Card>
              <p className="text-sm text-gray-400">Confirmadas</p>
              <p className="mt-2 text-2xl font-bold text-gray-100">
                {formatCurrency(report.totalActual)}
              </p>
            </Card>
            <Card>
              <p className="text-sm text-gray-400">Projetadas</p>
              <p className="mt-2 text-2xl font-bold text-amber-400">
                {formatCurrency(report.totalProjected)}
              </p>
            </Card>
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            <Card title={`Saldo — ${formatMonthYear(year, month)}`} className="lg:col-span-1">
              <p
                className={`text-4xl font-bold ${report.balance >= 0 ? 'text-income' : 'text-expense'}`}
              >
                {formatCurrency(report.balance)}
              </p>
            </Card>

            {chartData && report.categoryBreakdown.length > 0 && (
              <Card title="Despesas por categoria" className="lg:col-span-2">
                <div className="mx-auto max-w-xs">
                  <Doughnut
                    data={chartData}
                    options={{
                      plugins: { legend: { position: 'bottom', labels: { color: '#9ca3af' } } },
                    }}
                  />
                </div>
              </Card>
            )}
          </div>

          <Card title="Detalhamento">
            {report.expenses.length === 0 ? (
              <p className="text-gray-500">Nenhuma despesa neste mês</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-dark-border text-left text-gray-400">
                      <th className="pb-3 pr-4">Nome</th>
                      <th className="pb-3 pr-4">Categoria</th>
                      <th className="pb-3 pr-4">Tipo</th>
                      <th className="pb-3 pr-4">Status</th>
                      <th className="pb-3 text-right">Valor</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.expenses.map((e, i) => (
                      <tr key={`${e.name}-${i}`} className="border-b border-dark-border/50">
                        <td className="py-3 pr-4 text-gray-100">
                          {e.name}
                          {e.installmentNumber && e.totalInstallments && (
                            <span className="ml-1 text-xs text-gray-500">
                              ({e.installmentNumber}/{e.totalInstallments})
                            </span>
                          )}
                        </td>
                        <td className="py-3 pr-4">
                          <CategoryBadge category={e.category} />
                        </td>
                        <td className="py-3 pr-4 text-gray-400">
                          {EXPENSE_TYPE_LABELS[e.expenseType]}
                        </td>
                        <td className="py-3 pr-4">
                          <span
                            className={`rounded-full px-2 py-0.5 text-xs ${
                              e.status === 'actual'
                                ? 'bg-income/15 text-income'
                                : 'bg-amber-500/15 text-amber-400'
                            }`}
                          >
                            {e.status === 'actual' ? 'Confirmada' : 'Projetada'}
                          </span>
                        </td>
                        <td className="py-3 text-right font-medium text-expense">
                          {formatCurrency(e.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      ) : null}
    </div>
  );
}
